/**
 * `useUploadEvidence` (W6-F2-S1).
 *
 * TanStack Query mutation that uploads a file to Core's polymorphic
 * `documents` endpoint with `doc_type='assessment_evidence'`.
 *
 * Why XHR instead of `fetch`?
 *   The W2-F5 `documentClient.uploadDocument` is a single multipart POST —
 *   that's the only path Core supports today (no presigned-URL flow). The
 *   browser `fetch` API does not expose upload progress events, so this hook
 *   re-implements the multipart POST with `XMLHttpRequest` so we can wire a
 *   real per-file progress bar. The body shape (FormData fields) mirrors
 *   `packages/core-api-client/src/documents.ts → buildMultipartBody` so when
 *   Core ships presigned-URL support and the W2 client switches transparently,
 *   this hook can be replaced with a thin call to `documentClient.uploadDocument`
 *   without changing any of its consumers.
 *
 * Browsers without `xhr.upload` (none currently shipping in production) fall
 * back to the same XHR call but without progress events — the UI shows an
 * indeterminate spinner.
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { useAuth } from '@/auth/AuthProvider';
import { evidenceQueryKeys } from './query-keys';

const API_BASE_URL =
  ((import.meta as ImportMeta).env as Record<string, string | undefined>)
    .VITE_API_BASE_URL ?? 'http://localhost:3001';

export interface UploadEvidenceInput {
  buildingId: string;
  file: File;
  /** Defaults to `assessment_evidence` (the only doc-type W6 ships). */
  docType?: string;
  /** Free-text classification — Core requires non-empty. Defaults to `evidence`. */
  category?: string;
  /** Optional polymorphic attachment for upload-and-link. */
  attachedTo?: { entityType: 'asset_variable_value'; entityId: string };
  /** Free-form description forwarded to Core. */
  description?: string;
  /**
   * #1571 — validity-start date as ISO `YYYY-MM-DD`. Forwarded to Core
   * in `extra_data.valid_from` until a follow-up Core MR promotes the
   * column to first-class.
   */
  validFrom?: string | null;
  /**
   * #1571 — validity-end date as ISO `YYYY-MM-DD`. `null`/omitted =
   * indefinite. Forwarded to Core in `extra_data.valid_to`.
   */
  validTo?: string | null;
  /** Progress callback fired on every `xhr.upload.onprogress` event. */
  onProgress?: (percent: number) => void;
}

export interface UploadedDocument {
  id: string;
  filename: string;
  file_url: string;
  file_size_bytes?: number | null;
  file_type: string;
  doc_type?: string | null;
  building_id: string;
  entity_type?: string | null;
  entity_id?: string | null;
  created_at: string;
  uploaded_by?: string | null;
  /**
   * #1571 — validity-start date as ISO `YYYY-MM-DD`. Mirrored from
   * `extra_data.valid_from` by `useEvidenceList` until Core types it.
   */
  valid_from?: string | null;
  /** #1571 — validity-end date; null = indefinite. */
  valid_to?: string | null;
  /**
   * #1574 / Core MR !291 — per-link comment carried on the polymorphic
   * binding. Updated via `usePatchLinkComment`. Rendered next to the
   * linked-variable name in the Documentación tab + variables-view
   * drilldown.
   */
  link_comment?: string | null;
  /**
   * #1574 — when `true`, the document refuses to unlink to nothing
   * (`DELETE /documents/:id/links/:link_id` returns 409
   * `EVIDENCE_LAST_LINK`). Set by Core when the document was created
   * via the evidence-flow (upload-with-entity).
   */
  requires_link?: boolean;
  /** Core's JSONB stash — `useEvidenceList` reads valid_from / valid_to from here as fallback. */
  extra_data?: Record<string, unknown> | null;
}

export class EvidenceUploadError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(opts: { message: string; status: number; code: string }) {
    super(opts.message);
    this.name = 'EvidenceUploadError';
    this.status = opts.status;
    this.code = opts.code;
  }
}

/**
 * Performs the multipart POST via XHR so we can capture upload progress.
 * Returns the parsed Document on success, throws an `EvidenceUploadError`
 * otherwise.
 */
export async function uploadEvidenceXhr(
  input: UploadEvidenceInput & { token: string | null },
): Promise<UploadedDocument> {
  const form = new FormData();
  form.set('file', input.file, input.file.name);
  form.set('category', input.category ?? 'evidence');
  form.set('upload_source', 'senviro');
  form.set('doc_type', input.docType ?? 'assessment_evidence');
  if (input.description) form.set('description', input.description);
  if (input.attachedTo) {
    form.set('entity_type', input.attachedTo.entityType);
    form.set('entity_id', input.attachedTo.entityId);
  }
  // #1571 — stash validity dates in extra_data until Core types them.
  const extra: Record<string, string> = {};
  if (input.validFrom) extra.valid_from = input.validFrom;
  if (input.validTo) extra.valid_to = input.validTo;
  if (Object.keys(extra).length > 0) {
    form.set('extra_data', JSON.stringify(extra));
  }

  const url = `${API_BASE_URL}/api/v1/proxy/core/buildings/${encodeURIComponent(
    input.buildingId,
  )}/documents`;

  return new Promise<UploadedDocument>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    if (input.token) xhr.setRequestHeader('Authorization', `Bearer ${input.token}`);
    xhr.setRequestHeader('Accept', 'application/json');

    if (xhr.upload && typeof xhr.upload.addEventListener === 'function') {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && input.onProgress) {
          input.onProgress(event.loaded / event.total);
        }
      });
    }

    xhr.addEventListener('load', () => {
      const text = xhr.responseText ?? '';
      let parsed: unknown;
      try {
        parsed = text ? JSON.parse(text) : undefined;
      } catch {
        parsed = undefined;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        const wrapped = parsed as { data?: UploadedDocument } | UploadedDocument;
        const doc = (wrapped as { data?: UploadedDocument })?.data ?? (wrapped as UploadedDocument);
        if (input.onProgress) input.onProgress(1);
        resolve(doc);
      } else {
        const env = parsed as { error?: { message?: string; code?: string } } | undefined;
        reject(
          new EvidenceUploadError({
            message: env?.error?.message ?? `HTTP ${xhr.status}`,
            status: xhr.status,
            code: env?.error?.code ?? 'EVIDENCE_UPLOAD_FAILED',
          }),
        );
      }
    });
    xhr.addEventListener('error', () => {
      reject(
        new EvidenceUploadError({
          message: 'Network error during evidence upload',
          status: 0,
          code: 'EVIDENCE_UPLOAD_NETWORK',
        }),
      );
    });
    xhr.addEventListener('abort', () => {
      reject(
        new EvidenceUploadError({
          message: 'Upload aborted',
          status: 0,
          code: 'EVIDENCE_UPLOAD_ABORTED',
        }),
      );
    });
    xhr.send(form);
  });
}

export type UploadEvidenceMutation = UseMutationResult<
  UploadedDocument,
  EvidenceUploadError,
  UploadEvidenceInput
>;

/**
 * Mutation that uploads a single evidence file. Tracks per-file progress
 * via `onProgress`; the caller is responsible for storing the percentage in
 * its own UI state (the dropzone passes `setProgress` down).
 */
export function useUploadEvidence(): UploadEvidenceMutation {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<UploadedDocument, EvidenceUploadError, UploadEvidenceInput>({
    mutationFn: async (input) => {
      const token = await Promise.resolve(getAccessToken());
      return uploadEvidenceXhr({ ...input, token });
    },
    onSuccess: (doc, vars) => {
      // Invalidate every list query keyed on this building so the new doc
      // shows up immediately. Variable-scoped lists (W6-F3) get invalidated
      // separately via their own mutations.
      void queryClient.invalidateQueries({
        queryKey: evidenceQueryKeys.list({ buildingId: vars.buildingId }),
      });
      if (vars.attachedTo?.entityType === 'asset_variable_value') {
        void queryClient.invalidateQueries({
          queryKey: evidenceQueryKeys.byVariable(vars.attachedTo.entityId),
        });
      }
      // Returned doc itself isn't cached separately yet — F2-S2 may add a
      // `getDocument` query later; for now consumers re-list.
      return doc;
    },
  });
}
